import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { logError, logSuccess, getErrorMessage } from "../../../utils/errorHandler";

const DEFAULT_PLATFORM_CONFIG = {
  websiteClosed: false,
  websiteCloseMessage: "We are temporarily closed for maintenance. Please check back soon.",
  acceptOrders: true,
  codEnabled: true,
  codMaintenanceMessage: "Cash on Delivery is temporarily unavailable.",
  supportEmail: "shreejisurtiswad@gmail.com",
  supportPhone: "7977788566",
  supportWhatsApp: "8454874147",
};

export default function PlatformControl() {
  const [config, setConfig] = useState(DEFAULT_PLATFORM_CONFIG);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const ref = doc(db, "appConfig", "platform");
      const unsubscribe = onSnapshot(
        ref,
        (snap) => {
          try {
            if (!snap.exists()) {
              logSuccess("PlatformControl", "Using default config");
              setConfig(DEFAULT_PLATFORM_CONFIG);
              setLoading(false);
              return;
            }

            setConfig((prev) => ({
              ...prev,
              ...snap.data(),
            }));
            setLoading(false);
            setError("");
            logSuccess("PlatformControl", "Config loaded from database");
          } catch (err) {
            logError("PlatformControl Snapshot", err);
            setError(getErrorMessage(err));
            setLoading(false);
          }
        },
        (error) => {
          logError("PlatformControl Listener", error);
          setError(getErrorMessage(error));
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      logError("PlatformControl Setup", err);
      setError(getErrorMessage(err));
      setLoading(false);
    }
  }, []);

  const saveConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus("");
    setError("");

    try {
      logSuccess("PlatformControl", "Saving config...");
      
      await setDoc(
        doc(db, "appConfig", "platform"),
        {
          ...config,
          updatedAt: new Date().toISOString(),
          updatedAtMs: Date.now(),
        },
        { merge: true }
      );
      
      setStatus("✓ Platform settings saved successfully");
      logSuccess("PlatformControl", "Settings saved successfully");
    } catch (err) {
      logError("Save Config", err);
      setError(getErrorMessage(err));
      setStatus("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="platform-control-page">
      <h1>Platform & Checkout Control</h1>
      
      {/* 🔥 ERROR DISPLAY */}
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* 🔥 LOADING STATE */}
      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          ⏳ Loading platform configuration...
        </div>
      ) : (
        <form className="card platform-control-card" onSubmit={saveConfig}>
        <label className="switch-row">
          <input
            type="checkbox"
            checked={Boolean(config.websiteClosed)}
            onChange={(e) => setConfig((prev) => ({ ...prev, websiteClosed: e.target.checked }))}
          />
          <span>Temporarily close website</span>
        </label>

        <label className="switch-row">
          <input
            type="checkbox"
            checked={Boolean(config.acceptOrders)}
            onChange={(e) => setConfig((prev) => ({ ...prev, acceptOrders: e.target.checked }))}
          />
          <span>Accept new orders</span>
        </label>

        <label>Website close message</label>
        <textarea
          rows={3}
          value={config.websiteCloseMessage || ""}
          onChange={(e) =>
            setConfig((prev) => ({
              ...prev,
              websiteCloseMessage: e.target.value.slice(0, 240),
            }))
          }
        />

        <label className="switch-row">
          <input
            type="checkbox"
            checked={Boolean(config.codEnabled)}
            onChange={(e) => setConfig((prev) => ({ ...prev, codEnabled: e.target.checked }))}
          />
          <span>Enable Cash on Delivery (COD)</span>
        </label>

        <label>COD maintenance message</label>
        <input
          type="text"
          value={config.codMaintenanceMessage || ""}
          onChange={(e) =>
            setConfig((prev) => ({
              ...prev,
              codMaintenanceMessage: e.target.value.slice(0, 180),
            }))
          }
        />

        <label>Support email</label>
        <input
          type="email"
          value={config.supportEmail || ""}
          onChange={(e) => setConfig((prev) => ({ ...prev, supportEmail: e.target.value.trim() }))}
        />

        <label>Support phone</label>
        <input
          type="text"
          value={config.supportPhone || ""}
          onChange={(e) => setConfig((prev) => ({ ...prev, supportPhone: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) }))}
          maxLength={10}
        />

        <label>Support WhatsApp</label>
        <input
          type="text"
          value={config.supportWhatsApp || ""}
          onChange={(e) => setConfig((prev) => ({ ...prev, supportWhatsApp: e.target.value.replace(/[^0-9]/g, "").slice(0, 10) }))}
          maxLength={10}
        />

        {status && <p className="success-banner">{status}</p>}

        <button type="submit" disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </form>
      )}
    </div>
  );
}
